import { Badge as MantineBadge, Button, Group as MantineGroup, Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import { useState, useEffect } from 'react';
import { useDisclosure } from '@mantine/hooks';
import ModalDelete from '../courses/ModalDelete';
import ModalFilterCoursePlan, {
  CoursePlanParams as CoursePlanFilterParams
} from './ModalFilterCoursePlan';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { useCoursePlanStore } from 'store/createCoursePlan';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { coursePlanService, CoursePlanData } from 'services/lcms/coursePlanService';
import { notifications } from '@mantine/notifications';
import { Badge, Text, Group, LoadingOverlay } from '@mantine/core';
import Dates from 'components/Dates';

export interface CoursePlanParams {
  search?: string | null;
  programType?: string | null;
  status?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  page?: number;
  size?: number;
}

const TableCoursePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setFormMode, setFormData, reset } = useCoursePlanStore();
  const [searchParams] = useSearchParams();

  const [openedDelete, { open: openModalDelete, close: closeModalDelete }] = useDisclosure(false);
  const [openedFilter, { open: openModalFilter, close: closeModalFilter }] = useDisclosure(false);
  const [selectedRow, setSelectedRow] = useState<CoursePlanData | null>(null);

  // Following the same pattern as TableCourses
  const [params, setParams] = useState<CoursePlanParams>({
    programType: null,
    status: null,
    startDate: null,
    endDate: null
  });

  // Extract URL search params like TableCourses does
  const search = searchParams.get('search');
  const programType = searchParams.get('programType');
  const status = searchParams.get('status');
  const sortBy = searchParams.get('sortBy');
  const sortOrder = searchParams.get('sortOrder');
  const pageNumber = searchParams.get('page');
  const pageSize = searchParams.get('size');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  // Query with all search params as dependencies (like TableCourses)
  const { data, isLoading } = useQuery({
    queryKey: [
      'coursePlans',
      search,
      programType,
      status,
      sortBy,
      sortOrder,
      pageNumber,
      pageSize,
      startDate,
      endDate
    ],
    queryFn: () =>
      coursePlanService.getCoursePlanList({
        search: search ?? '',
        programType: programType ?? undefined,
        status: status ?? undefined,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        sortBy: sortBy ?? 'datein',
        sortOrder: (sortOrder as 'asc' | 'desc') ?? 'desc',
        pageNumber: Number(pageNumber) || 1,
        pageSize: Number(pageSize) || 10
      }),
    refetchOnWindowFocus: false
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => coursePlanService.deleteCoursePlan(id),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Course plan deleted successfully',
        color: 'green'
      });
      queryClient.invalidateQueries({ queryKey: ['coursePlans'] });
      closeModalDelete();
      setSelectedRow(null);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete course plan',
        color: 'red'
      });
    }
  });

  // Update URL params when params change (like TableCourses)
  useEffect(() => {
    const urlParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value instanceof Date) {
        urlParams.set(key, value.toISOString());
      } else if (value !== null && value !== undefined && value !== '') {
        urlParams.set(key, value.toString());
      }
    });

    const next = `${location.pathname}?${urlParams.toString()}`;
    const current = `${location.pathname}${location.search}`;

    if (next !== current) {
      navigate(next, { replace: true });
    }
  }, [params, searchParams, navigate, location.pathname, location.search]);

  // Define columns
  const [columns, setColumns] = useState([
    {
      heading: 'Plan Name',
      props: (row: CoursePlanData) => (
        <div>
          <Text fw={500} size='sm' lineClamp={1}>
            {row.planningName}
          </Text>
          <Text size='xs' c='dimmed' maw={200} lineClamp={2}>
            {row.planningDescription}
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'planningName'
    },
    {
      heading: 'Program Type',
      props: (row: CoursePlanData) => {
        const getColor = () => {
          if (row.programType === 'modular') return 'blue';
          if (row.programType === 'program') return 'green';
          if (row.programType === 'emerging needs') return 'orange';
          return 'gray';
        };
        return (
          <Badge variant='light' color={getColor()} size='sm'>
            {row.programType}
          </Badge>
        );
      },
      sortable: true,
      active: true,
      id: 'programType'
    },
    {
      heading: 'Schedule',
      props: (row: CoursePlanData) => (
        <div>
          <Text size='sm'>
            <Dates date={row.planningStart} fz={12} />
          </Text>
          <Text size='xs' c='dimmed'>
            to <Dates date={row.planningEnd} fz={12} />
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'planningStart'
    },
    {
      heading: 'Summary',
      props: (row: CoursePlanData) => (
        <Group gap='xs'>
          <Badge variant='outline' size='xs'>
            {row.totalCourses} courses
          </Badge>
          <Badge variant='outline' size='xs'>
            {row.totalPositions} positions
          </Badge>
        </Group>
      ),
      active: true,
      id: 'summary'
    },

    {
      heading: 'Created',
      props: (row: CoursePlanData) => (
        <div>
          <Text size='sm'>
            <Dates date={row.dateIn} fz={12} />
          </Text>
          <Text size='xs' c='dimmed'>
            by {row.userIn}
          </Text>
        </div>
      ),
      sortable: true,
      active: true,
      id: 'datein'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ]);

  const handleAdd = () => {
    reset();
    setFormMode('add');
    navigate(pathnames.createCoursePlan);
  };

  const handleDelete = (row: CoursePlanData) => {
    setSelectedRow(row);
    openModalDelete();
  };

  const handleEdit = (row: CoursePlanData) => {
    setFormMode('edit');
    setFormData({
      courseId: row.msLCMSPlanningID,
      planName: row.planningName,
      programType: row.programType,
      description: row.planningDescription,
      startTime: row.planningStart,
      endTime: row.planningEnd,
      notes: row.notes,
      courses: [],
      targetPosition: ''
    });
    navigate(`${pathnames.coursePlan}/edit/${row.msLCMSPlanningID}`);
  };

  const handleView = (row: CoursePlanData) => {
    setFormMode('view');
    setFormData({
      courseId: row.msLCMSPlanningID,
      planName: row.planningName,
      programType: row.programType,
      description: row.planningDescription,
      startTime: row.planningStart,
      endTime: row.planningEnd,
      notes: row.notes,
      courses: [],
      targetPosition: ''
    });
    navigate(`${pathnames.coursePlan}/${row.msLCMSPlanningID}`);
  };

  // Pagination handlers like TableCourses
  const handleChangeSize = (size: number) =>
    setParams((prevState) => {
      return { ...prevState, size };
    });

  const handleChangePage = (page: number) =>
    setParams((prevState) => {
      return { ...prevState, page };
    });

  const handleFilter = (data: CoursePlanFilterParams) => {
    const newParams = new URLSearchParams(searchParams);

    setParams((prev) => ({ ...prev, page: 1 }));
    newParams.set('page', '1');

    if (data.programType) {
      newParams.set('programType', data.programType);
    } else {
      newParams.delete('programType');
    }

    if (data.status) {
      newParams.set('status', data.status);
    } else {
      newParams.delete('status');
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleClearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    setParams((prev) => ({ ...prev, page: 1 }));
    newParams.set('page', '1');
    newParams.delete('programType');
    newParams.delete('status');
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const processDelete = async () => {
    if (selectedRow) {
      await deleteMutation.mutateAsync(selectedRow.msLCMSPlanningID);
    }
  };

  return (
    <>
      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || deleteMutation.isPending} />

        <Stack>
          <Toolbar
            columns={columns}
            showSearch
            showCreateButton
            showFilter
            searchPlaceholder='Search course plans...'
            createButtonText='New Plan'
            onCreate={handleAdd}
            onFilter={openModalFilter}
            showFilterColumn={true}
            setColumns={setColumns}
          />

          {(programType || status) && (
            <MantineGroup gap='xs'>
              {programType && (
                <MantineBadge color='violet'>Program Type: {programType}</MantineBadge>
              )}
              {status && <MantineBadge color='violet'>Status: {status}</MantineBadge>}
              <Button size='xs' variant='light' onClick={handleClearFilters}>
                Clear filters
              </Button>
            </MantineGroup>
          )}

          <DataTables
            columns={columns}
            data={data?.data || []}
            loading={isLoading}
            totalData={data?.totalRecords}
            currentPage={Number(pageNumber) || 1}
            currentSize={Number(pageSize) || 10}
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleChangeSize={handleChangeSize}
            handleChangePage={handleChangePage}
            emptyMessageTitle='No course plans found'
            emptyMessageDescription='Create your first course plan by clicking the New Plan button'
          />
        </Stack>
      </Card>

      <ModalDelete
        title='Delete Course Plan'
        description={`You are about to delete course plan "${selectedRow?.planningName}". Are you sure you want to delete it?`}
        labelConfirm='Delete'
        onClose={closeModalDelete}
        onConfirm={processDelete}
        opened={openedDelete}
        isLoading={deleteMutation.isPending}
      />

      <ModalFilterCoursePlan
        defaultValue={{
          programType: programType || null,
          status: status || null
        }}
        onClose={closeModalFilter}
        onFilter={handleFilter}
        opened={openedFilter}
      />
    </>
  );
};

export default TableCoursePlan;
